import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Users, BookOpen, ClipboardList, CheckCircle2, AlertTriangle,
  Loader2, ArrowRight, XCircle
} from 'lucide-react';
import { executeImport } from '../utils/executeImport';
import { useAuth } from '@/contexts/AuthContext';

export function ConfirmImportStep({
  userMatches,
  qualMatches,
  parsedAssignments,
  projectId,
  onComplete
}) {
  const { company, userProfile } = useAuth();
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ phase: '', current: 0, total: 0, detail: '' });
  const [results, setResults] = useState(null);

  const usersToMatch = userMatches.filter(u => u.action === 'match').length;
  const usersToCreate = userMatches.filter(u => u.action === 'create_new').length;
  const usersSkipped = userMatches.filter(u => u.action === 'skip').length;

  const qualsExisting = qualMatches.filter(q => q.action === 'use_existing').length;
  const qualsToCreate = qualMatches.filter(q => q.action === 'create_from_course' || q.action === 'create_custom').length;
  const qualsSkipped = qualMatches.filter(q => q.action === 'skip').length;

  const assignmentsTotal = parsedAssignments.length;

  const handleImport = useCallback(async () => {
    if (!company?.id || !userProfile?.id || !projectId) return;
    setImporting(true);

    try {
      const result = await executeImport({
        users: userMatches,
        qualifications: qualMatches,
        assignments: parsedAssignments,
        projectId,
        companyId: company.id,
        userProfileId: userProfile.id,
        onProgress: (phase, current, total, detail) => {
          setProgress({ phase, current, total, detail });
        }
      });

      setResults(result.results);
      onComplete?.(result);
    } catch (err) {
      console.error('Import failed:', err);
      setResults({
        errors: [{ message: `Import failed: ${err.message}` }],
        usersCreated: 0, usersMatched: 0, usersSkipped: 0,
        qualsCreated: 0, qualsExisting: 0, qualsSkipped: 0,
        assignmentsCreated: 0, assignmentsFailed: 0
      });
    } finally {
      setImporting(false);
    }
  }, [company?.id, userProfile?.id, projectId, userMatches, qualMatches, parsedAssignments, onComplete]);

  const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  const phaseLabels = {
    users: 'Creating users...',
    users_space: 'Adding users to space...',
    qualifications: 'Creating qualifications...',
    assignments: 'Creating assignments...',
    done: 'Complete!'
  };

  // Results view
  if (results) {
    const hasErrors = results.errors && results.errors.length > 0;

    return (
      <div className="space-y-5">
        <div className="text-center py-4">
          {hasErrors ? (
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          ) : (
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
          )}
          <h3 className="text-lg font-semibold">
            {hasErrors ? 'Import Completed with Warnings' : 'Import Successful!'}
          </h3>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-5 w-5 mx-auto mb-1.5 text-blue-600" />
              <p className="text-2xl font-bold">{results.usersCreated}</p>
              <p className="text-xs text-muted-foreground">Users Created</p>
              <p className="text-xs text-muted-foreground">{results.usersMatched} matched</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <BookOpen className="h-5 w-5 mx-auto mb-1.5 text-purple-600" />
              <p className="text-2xl font-bold">{results.qualsCreated}</p>
              <p className="text-xs text-muted-foreground">Qualifications Created</p>
              <p className="text-xs text-muted-foreground">{results.qualsExisting} existing</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <ClipboardList className="h-5 w-5 mx-auto mb-1.5 text-green-600" />
              <p className="text-2xl font-bold">{results.assignmentsCreated}</p>
              <p className="text-xs text-muted-foreground">Assignments Created</p>
              {results.assignmentsFailed > 0 && (
                <p className="text-xs text-red-500">{results.assignmentsFailed} failed</p>
              )}
            </CardContent>
          </Card>
        </div>

        {hasErrors && (
          <div className="border border-amber-200 rounded-lg bg-amber-50">
            <div className="px-3 py-2 border-b border-amber-200">
              <p className="text-sm font-medium text-amber-800">
                {results.errors.length} warning{results.errors.length !== 1 ? 's' : ''}
              </p>
            </div>
            <ScrollArea className="max-h-[150px]">
              <div className="p-3 space-y-1">
                {results.errors.map((err, i) => (
                  <p key={i} className="text-xs text-amber-700 flex items-start gap-1.5">
                    <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    {err.message || err}
                  </p>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    );
  }

  // Importing progress view
  if (importing) {
    return (
      <div className="space-y-6 py-8">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-1">Importing Data</h3>
          <p className="text-sm text-muted-foreground">
            {phaseLabels[progress.phase] || 'Preparing...'}
          </p>
          {progress.detail && (
            <p className="text-xs text-muted-foreground mt-1">{progress.detail}</p>
          )}
        </div>
        <div className="max-w-md mx-auto">
          <Progress value={progressPercent} className="h-2" />
          <p className="text-xs text-muted-foreground text-center mt-2">
            {progress.current} / {progress.total}
          </p>
        </div>
      </div>
    );
  }

  // Confirmation view (pre-import)
  return (
    <div className="space-y-5">
      <div className="text-center py-2">
        <h3 className="text-lg font-semibold">Ready to Import</h3>
        <p className="text-sm text-muted-foreground">Review the summary below and click Import to proceed</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-5 w-5 text-blue-600" />
              <h4 className="text-sm font-semibold">Users</h4>
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Match existing:</span>
                <Badge variant="secondary" className="text-xs">{usersToMatch}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Create new:</span>
                <Badge variant="secondary" className="text-xs">{usersToCreate}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Skip:</span>
                <Badge variant="secondary" className="text-xs">{usersSkipped}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="h-5 w-5 text-purple-600" />
              <h4 className="text-sm font-semibold">Qualifications</h4>
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Use existing:</span>
                <Badge variant="secondary" className="text-xs">{qualsExisting}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Create new:</span>
                <Badge variant="secondary" className="text-xs">{qualsToCreate}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Skip:</span>
                <Badge variant="secondary" className="text-xs">{qualsSkipped}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="h-5 w-5 text-green-600" />
              <h4 className="text-sm font-semibold">Assignments</h4>
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total:</span>
                <Badge variant="secondary" className="text-xs">{assignmentsTotal}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-center pt-4">
        <Button
          onClick={handleImport}
          size="lg"
          className="bg-black hover:bg-gray-900 text-white px-8"
          disabled={assignmentsTotal === 0}
        >
          <ArrowRight className="h-4 w-4 mr-2" />
          Start Import
        </Button>
      </div>

      {assignmentsTotal === 0 && (
        <p className="text-xs text-amber-600 text-center">
          No assignments to import. Go back and check your mapping and user/qualification configuration.
        </p>
      )}
    </div>
  );
}
